import { useState, useEffect, useRef } from 'react'
import {
  subscribeToChatStream,
  type ChatStreamDocument,
  type ChatStreamStatus,
} from '../services/chat-stream.service'
import type { Firestore } from 'firebase/firestore'

const STREAM_TIMEOUT_MS = 60_000

interface UseChatStreamOptions {
  db: Firestore
  streamId: string | null
  enabled?: boolean
  onTextUpdate?: (text: string) => void
  onComplete?: (finalText: string, finalHtml: string, metadata?: Record<string, unknown>) => void
  onError?: (error: string) => void
}

interface UseChatStreamReturn {
  streamData: ChatStreamDocument | null
  isStreaming: boolean
  isCompleted: boolean
  hasError: boolean
  error: string | null
  text: string
  html: string
  status: ChatStreamStatus | null
  thinkingStep: string | null
  thoughtText: string | null
}

export const useChatStream = ({
  db,
  streamId,
  enabled = true,
  onTextUpdate,
  onComplete,
  onError,
}: UseChatStreamOptions): UseChatStreamReturn => {
  const [streamData, setStreamData] = useState<ChatStreamDocument | null>(null)
  const [error, setError] = useState<string | null>(null)
  const previousTextRef = useRef<string>('')
  const completedRef = useRef<boolean>(false)
  const onTextUpdateRef = useRef(onTextUpdate)
  const onCompleteRef = useRef(onComplete)
  const onErrorRef = useRef(onError)
  onTextUpdateRef.current = onTextUpdate
  onCompleteRef.current = onComplete
  onErrorRef.current = onError

  const isStreaming = streamData?.status === 'streaming' || streamData?.status === 'pending'
  const isCompleted = streamData?.status === 'completed'
  const hasError = streamData?.status === 'error' || !!error

  useEffect(() => {
    if (!streamId || !enabled) return

    completedRef.current = false
    previousTextRef.current = ''
    setStreamData(null)
    setError(null)

    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const clearInactivityTimeout = () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
    }

    const resetInactivityTimeout = () => {
      clearInactivityTimeout()
      timeoutId = setTimeout(() => {
        onErrorRef.current?.('Response timed out. Please try again.')
      }, STREAM_TIMEOUT_MS)
    }

    resetInactivityTimeout()

    const unsubscribe = subscribeToChatStream(
      db,
      streamId,
      (data) => {
        setStreamData(data)
        setError(null)

        if (data) {
          resetInactivityTimeout()

          if (data.text !== previousTextRef.current) {
            previousTextRef.current = data.text
            onTextUpdateRef.current?.(data.text)
          }

          if (data.status === 'completed' && !completedRef.current) {
            completedRef.current = true
            clearInactivityTimeout()
            onCompleteRef.current?.(data.text, data.html, data.metadata)
          }

          if (data.status === 'error' && data.error) {
            clearInactivityTimeout()
            setError(data.error)
            onErrorRef.current?.(data.error)
          }
        }
      },
      (err) => {
        clearInactivityTimeout()
        const errorMessage = err.message || 'Failed to subscribe to chat stream'
        setError(errorMessage)
        onErrorRef.current?.(errorMessage)
      }
    )

    return () => {
      clearInactivityTimeout()
      unsubscribe()
    }
  }, [streamId, enabled, db])

  return {
    streamData,
    isStreaming,
    isCompleted,
    hasError,
    error,
    text: streamData?.text || '',
    html: streamData?.html || '',
    status: streamData?.status || null,
    thinkingStep: streamData?.thinkingStep ?? null,
    thoughtText: streamData?.thoughtText ?? null,
  }
}

export default useChatStream
